import React, { useCallback, useEffect, useMemo, useState } from "react";
import withholdingTaxAPI from "../../api/withholdingTaxAPI";
import Button from "../Button";
import ReturnPaymentGl from "./ReturnPaymentGl";
import MoneyInput from "../MoneyInput";
import {
  MONTHS, manilaToday, bir_date, statusChips, Chip, displayValue, ItemRow, Part, inputClass,
  TaxpayerSizeBanner, FiledBadge, PaymentDetails, paymentsFromView, paymentPayload, mapFieldErrors,
  FileConfirmModal, useReturnActions,
} from "./returnUi";
import { pesosToCentavos } from "../../utils/whtMoney";

// BIR Form 0619-E (January 2018) for one client and year — docs/WHT_IMPLEMENTATION.md §5, D8.
// Items are shown with their BIR numbers so staff can copy them into
// eBIRForms (no PDF). Months 3/6/9/12 are covered by 1601-EQ and cannot be
// picked. Filed returns are read-only; "Amend" starts a new version.

export const FILING_MONTHS = [1, 2, 4, 5, 7, 8, 10, 11];

// Kept for existing callers/tests; the shared versions live in returnUi.jsx.
export { manilaToday, bir_date };
export const monthChips = statusChips;

const PART_I = [
  ["1", "For the month (MM/YYYY)", "text"],
  ["2", "Due date (MM/DD/YYYY)", "date"],
  ["3", "Amended form?", "bool"],
  ["4", "Any taxes withheld?", "bool"],
  ["5", "ATC", "text"],
  ["6", "Tax type code", "text"],
];
const PART_II_BG = [
  ["7", "Taxpayer Identification Number (TIN) – branch code", "text"],
  ["8", "RDO code", "text"],
  ["9", "Withholding agent's name", "text"],
  ["10", "Registered address", "text"],
  ["10A", "ZIP code", "text"],
  ["11", "Contact number", "text"],
  ["12", "Category of withholding agent", "category"],
  ["13", "Email address", "text"],
];
const PART_II_TAX = [
  ["14", "Amount of remittance", "money"],
  ["15", "Less: amount remitted from previously filed form, if this is an amended form", "money"],
  ["16", "Net amount of remittance (item 14 less item 15)", "money"],
  ["17A", "Add: penalties — surcharge", "money"],
  ["17B", "Interest", "money"],
  ["17C", "Compromise", "money"],
  ["17D", "Total penalties (sum of items 17A to 17C)", "money"],
  ["18", "Total amount of remittance (sum of items 16 and 17D)", "money"],
];
const PAYMENT_ROWS = [
  ["19", "cash", "Cash / bank debit memo"],
  ["20", "check", "Check"],
  ["21", "tdm", "Tax debit memo"],
  ["22", "other", "Others (specify)"],
];

const Form0619ETab = ({ client, year, onOpenPostingAccounts }) => {
  const clientId = client?._id;
  const today = manilaToday();
  const [summary, setSummary] = useState(null);
  const [month, setMonth] = useState(null);
  const [view, setView] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(null); // local edits: { payments, penaltyAuto, surcharge, interest, compromise }
  const [fieldErrors, setFieldErrors] = useState({});
  const [confirmFile, setConfirmFile] = useState(false);
  const [filedDate, setFiledDate] = useState(today);

  const loadSummary = useCallback(async () => {
    if (!clientId || !year) return;
    try {
      const res = await withholdingTaxAPI.get0619eYear(clientId, year);
      setSummary(res.data);
    } catch (e) {
      setError(e.message);
    }
  }, [clientId, year]);

  // Default month: the first filing month not yet filed, else the last one.
  useEffect(() => { setMonth(null); setView(null); loadSummary(); }, [loadSummary]);
  useEffect(() => {
    if (month || !summary) return;
    const open = summary.months.find((m) => m.status !== "filed");
    setMonth((open || summary.months[summary.months.length - 1]).month);
  }, [summary, month]);

  const loadView = useCallback(async () => {
    if (!clientId || !year || !month) return;
    setLoading(true);
    setError(null);
    try {
      const res = await withholdingTaxAPI.get0619e(clientId, year, month);
      setView(res.data);
      setDraft({
        payments: paymentsFromView(PAYMENT_ROWS, res.data.payment),
        penaltyAuto: res.data.penaltyAuto !== false,
        surcharge: (res.data.manualSurchargeCentavos || 0) / 100,
        interest: (res.data.manualInterestCentavos || 0) / 100,
        compromise: (res.data.manualCompromiseCentavos || 0) / 100,
      });
      setFieldErrors({});
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [clientId, year, month]);

  useEffect(() => { loadView(); }, [loadView]);

  const draftPayload = () => ({
    payment: paymentPayload(PAYMENT_ROWS, draft.payments),
    penaltyAuto: draft.penaltyAuto,
    manualSurchargeCentavos: pesosToCentavos(draft.surcharge) || 0,
    manualInterestCentavos: pesosToCentavos(draft.interest) || 0,
    manualCompromiseCentavos: pesosToCentavos(draft.compromise) || 0,
  });

  const afterChange = async (data) => {
    setView(data);
    await Promise.all([loadSummary(), loadView()]);
  };

  const { runSave, saving, runFile, filing, runAmend, amending } = useReturnActions({
    label: `0619-E · ${MONTHS[(month || 1) - 1]} ${year}`,
    saveRequest: () => {
      const payload = draftPayload();
      return withholdingTaxAPI.save0619eDraft(clientId, year, month, payload).catch((e) => {
        e.payload = payload;
        throw e;
      });
    },
    fileRequest: (filedAt) => withholdingTaxAPI.file0619e(clientId, year, month, filedAt),
    amendRequest: () => withholdingTaxAPI.amend0619e(clientId, year, month),
    onChanged: afterChange,
    onSaveError: (e) => setFieldErrors(mapFieldErrors(e, e.payload)),
    onFiled: () => setConfirmFile(false),
  });

  const summaryByMonth = useMemo(() => new Map((summary?.months || []).map((m) => [m.month, m])), [summary]);
  const readOnly = view?.readOnly;
  const items = view?.items || {};
  const setPayment = (type, patch) => setDraft((d) => ({ ...d, payments: { ...d.payments, [type]: { ...d.payments[type], ...patch } } }));

  return (
    <div>
      {/* Month picker + status */}
      <div role="group" aria-label="Month" className="grid grid-cols-3 md:grid-cols-6 lg:grid-cols-12 gap-2 mb-4">
        {MONTHS.map((label, i) => {
          const m = i + 1;
          const allowed = FILING_MONTHS.includes(m);
          const s = summaryByMonth.get(m);
          const selected = month === m;
          return (
            <button
              key={label}
              type="button"
              disabled={!allowed}
              aria-pressed={selected}
              title={allowed ? undefined : "Covered by 1601-EQ"}
              onClick={() => setMonth(m)}
              className={`flex flex-col items-start gap-1 rounded-md border px-2 py-2 text-left text-sm ${
                selected ? "border-primary-500 bg-primary-50" : "border-ink-line bg-white"
              } disabled:bg-ink-canvas disabled:text-ink-body disabled:cursor-not-allowed`}
            >
              <span className="font-medium text-ink">{label}</span>
              {allowed && s && (
                <span className="flex flex-wrap gap-1" data-testid={`chips-${m}`}>
                  {statusChips(s, today).map((c) => <Chip key={c} label={c} />)}
                </span>
              )}
              {!allowed && <span className="text-xs">1601-EQ</span>}
            </button>
          );
        })}
      </div>

      {error && <p role="alert" className="text-sm text-danger-500 mb-3">{error}</p>}
      {loading && !view && <p className="text-sm text-ink-body">Loading…</p>}

      {view && draft && (
        <div>
          {view.flags?.includes("NEEDS_TAXPAYER_SIZE") && <TaxpayerSizeBanner clientId={clientId} />}

          <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
            <div className="flex items-center gap-2">
              <h3 className="text-lg font-medium text-ink">BIR Form 0619-E · {MONTHS[month - 1]} {year}</h3>
              {view.isAmended && <Chip label={readOnly ? "Amended" : "Amendment draft"} />}
              {readOnly && <FiledBadge filedAt={view.filedAt} />}
            </div>
            <div className="flex items-center gap-2">
              {readOnly ? (
                <Button variant="outline" onClick={() => runAmend()} loading={amending}>Amend</Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => runSave()} loading={saving}>Save draft</Button>
                  <Button onClick={() => setConfirmFile(true)}>Mark as filed</Button>
                </>
              )}
            </div>
          </div>

          {!readOnly && view.penaltyAuto && (
            <p className="text-xs text-ink-body mb-3">
              Due {bir_date(view.adjustedDueDate)}. Penalties are shown as if filed today ({bir_date(view.penaltyAsOf)}) and are recomputed with the filing date.
            </p>
          )}

          {/* G2: payment journal entry of the filed version. */}
          <ReturnPaymentGl client={client} form="0619e" view={view} onOpenPostingAccounts={onOpenPostingAccounts}
            onChanged={() => Promise.all([loadSummary(), loadView()])} />

          <Part title="Part I – Background information">
            {[...PART_I, ...PART_II_BG].map(([no, label, kind]) => (
              <ItemRow key={no} no={no} label={label}>{displayValue(kind, items[no])}</ItemRow>
            ))}
          </Part>

          <Part title="Part II – Computation of tax">
            {PART_II_TAX.map(([no, label, kind]) => {
              const editable = !readOnly && (no === "17C" || (!draft.penaltyAuto && (no === "17A" || no === "17B")));
              const field = { "17A": "surcharge", "17B": "interest", "17C": "compromise" }[no];
              return (
                <ItemRow key={no} no={no} label={label}>
                  {editable ? (
                    <MoneyInput
                      id={`item-${no}-input`}
                      value={draft[field]}
                      onValueChange={(v) => setDraft((d) => ({ ...d, [field]: v }))}
                      className={`${inputClass} w-36 text-right`}
                    />
                  ) : displayValue(kind, items[no])}
                </ItemRow>
              );
            })}
            {!readOnly && (
              <label className="flex items-center gap-2 text-sm text-ink mt-3">
                <input
                  type="checkbox"
                  checked={draft.penaltyAuto}
                  onChange={(e) => setDraft((d) => ({ ...d, penaltyAuto: e.target.checked }))}
                  className="h-4 w-4 accent-primary-500"
                />
                Compute surcharge and interest automatically (the compromise is always entered by hand)
              </label>
            )}
            {!readOnly && <p className="text-xs text-ink-body mt-1">Save the draft to recompute items 17D and 18.</p>}
          </Part>

          <PaymentDetails
            title="Part III – Details of payment"
            rows={PAYMENT_ROWS}
            payments={draft.payments}
            readOnly={readOnly}
            fieldErrors={fieldErrors}
            onChange={setPayment}
          />

          <FileConfirmModal
            isOpen={confirmFile}
            title={`Mark the ${MONTHS[month - 1]} ${year} 0619-E as filed?`}
            filedDate={filedDate}
            onDateChange={setFiledDate}
            loading={filing}
            onConfirm={() => runFile(filedDate)}
            onCancel={() => setConfirmFile(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Form0619ETab;
